// Afstemning af regime-foreslåede §54-klausuler med wizard-state. Ren logik (ingen React),
// så den kan bruges både fra wizardens state-opdatering og fra Vilkår-trinnet.
//
// Regler:
//  - Samme kombination (key) som sidst: rør intet – landlords fravalg består.
//  - Ny kombination: fjern kun de klausuler der blev auto-tilføjet (applied.added); tilføj
//    de nye, men aldrig en der allerede er valgt, og aldrig en landlord selv har fravalgt
//    (applied.dismissed).
//  - Manuelt valg/fravalg (noteManualChange): et manuelt tilføjet id ejes af landlord
//    (fjernes aldrig automatisk); et fravalgt auto-id huskes i dismissed og genindsættes
//    ikke, heller ikke hvis man skifter til en anden kombination og tilbage.
//  - Fri leje slået fra: regimet nulstilles og de auto-tilføjede klausuler fjernes.

import { regimeClauses } from './regimeClauses'
import type { RentRegime } from './regimeClauses'

export interface RegimeApplied {
  key: string
  added: string[]
  dismissed: string[]
}

export interface RegimeInput {
  freeRent: boolean
  reason: string
  regime: RentRegime | undefined
}

export interface RegimeReconciled {
  ids: string[]
  applied: RegimeApplied
  regime: RentRegime
}

export function reconcileRegime(
  ids: string[],
  input: RegimeInput,
  applied: RegimeApplied | undefined,
): RegimeReconciled {
  const regime: RentRegime = input.freeRent ? (input.regime ?? '') : ''
  const res = regimeClauses(input.freeRent, input.reason, regime)
  const key = res.status === 'afklaret' ? `${regime}:${input.reason}` : 'uafklaret'
  const prev: RegimeApplied = applied ?? { key: '', added: [], dismissed: [] }
  if (prev.key === key) return { ids, applied: prev, regime }
  const prevAdded = new Set(prev.added)
  const kept = ids.filter(id => !prevAdded.has(id))
  const toAdd = res.ids.filter(id => !kept.includes(id) && !prev.dismissed.includes(id))
  return {
    ids: [...kept, ...toAdd],
    applied: { key, added: toAdd, dismissed: prev.dismissed },
    regime,
  }
}

/** Landlord har selv tilføjet (nowActive) eller fjernet et klausul-id. */
export function noteManualChange(
  applied: RegimeApplied | undefined,
  id: string,
  nowActive: boolean,
): RegimeApplied | undefined {
  if (!applied) return applied
  if (nowActive) {
    return {
      ...applied,
      added: applied.added.filter(x => x !== id),
      dismissed: applied.dismissed.filter(x => x !== id),
    }
  }
  if (applied.added.includes(id)) {
    return {
      ...applied,
      added: applied.added.filter(x => x !== id),
      dismissed: [...applied.dismissed, id],
    }
  }
  return applied
}
